'use client';

import { useMemo } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { AlertTriangle } from 'lucide-react';
import type { GridMatch } from '@/types/tournament.model';
import { cn } from '@/lib/utils';
import type {
  GridModel,
  GridSlotRef,
  PlaceholderLabelMap,
  TournamentBadge,
} from './grid-model';
import { buildDay, type DayRowModel } from './court-model';
import { capacityLabel, cellStateClasses, verdictReasonClass } from './cell-presentation';
import { dropVerdictFor, type DropVerdict } from './drop-state';
import { GridMatchCard } from './GridMatchCard';
import { GridAvailableTeamsButton } from './GridAvailableTeamsButton';

/**
 * Court columns share whatever width is available instead of being pinned to a fixed size:
 * `flex-1` lets them grow, `min-w` refuses to shrink past legibility (so they overflow and the
 * container scrolls when there are many courts), and `max-w` stops a single court from stretching
 * a card across an entire wide screen.
 */
const COURT_WIDTH = 'min-w-[180px] max-w-[360px] flex-1';

/** The time gutter. Fixed on both the header and the rows so the columns line up. */
const TIME_GUTTER = 'w-[72px] shrink-0';

/**
 * The same numbers the two class strings above encode, plus the `gap-2` between columns and the
 * rows' own `p-2`. They have to be repeated as literals there and as numbers here: Tailwind
 * extracts class names statically, so an interpolated `min-w-[${n}px]` would never be generated.
 * Keep the pairs in sync.
 */
const COURT_MIN_PX = 180;
const GAP_PX = 8;
const TIME_GUTTER_PX = 72;
const ROW_PADDING_PX = 16;

/**
 * How wide the day has to be for every column to fit at its minimum, rows included.
 *
 * A row paints its own background and border, and a block-level row is only ever as wide as the
 * box that contains it — so with the container pinned to `min-w-full`, the columns overflowed to
 * the right while the row's background stopped at the screen edge: scrolled sideways on a phone,
 * half the day was grey and half was bare page. Sizing the container to what the columns actually
 * need makes the rows span the whole scrollable width.
 */
function dayMinWidth(courtCount: number): string {
  const columns = TIME_GUTTER_PX + courtCount * (COURT_MIN_PX + GAP_PX);
  // `max()` and not a plain value: when the screen is wider than that, 100% wins and `flex-1`
  // spreads the courts over the space instead of leaving the day squeezed to the left.
  return `max(100%, ${ROW_PADDING_PX + columns}px)`;
}

interface GridDayViewProps {
  model: GridModel;
  date: string;
  /** tournamentId → its position in `grid.tournaments`, for the derived court order. */
  tournamentOrder: Map<string, number>;
  badgeOf: (id: string) => TournamentBadge;
  pendingMatchIds: Set<string>;
  editDisabled: boolean;
  onEditMatch: (match: GridMatch) => void;
  /** matchId -> qualifier labels for its undecided bracket slots. */
  placeholderLabels: PlaceholderLabelMap;
  dragging: GridMatch | null;
  /** Opens the "who can play" list for a slot. */
  onShowAvailableTeams: (slot: GridSlotRef) => void;
}

/**
 * One day split into courts. Courts are derived from the order inside each cell — see
 * `sortForCourts` — so a match cannot be dropped onto a *specific* court: there is nowhere to
 * store that. Dropping anywhere on a row means "move to this time", and the whole row highlights
 * during a drag so the UI doesn't promise a precision it doesn't have.
 */
export function GridDayView({
  model,
  date,
  tournamentOrder,
  badgeOf,
  pendingMatchIds,
  editDisabled,
  onEditMatch,
  placeholderLabels,
  dragging,
  onShowAvailableTeams,
}: GridDayViewProps) {
  // Memoized because `dragging` changing (at the start and end of every drag) would otherwise
  // rebuild the whole day — a `sortForCourts` per time slot — for an identical result.
  const day = useMemo(
    () => buildDay(model, date, tournamentOrder),
    [model, date, tournamentOrder],
  );

  if (day.rows.length === 0) {
    return (
      <p className="text-sm text-gray-500">
        {model.hiddenTimes.length > 0
          ? 'Todos los horarios de este día están ocultos.'
          : 'Este día no tiene franjas ni partidos.'}
      </p>
    );
  }

  return (
    <div className="overflow-x-auto">
      <div className="space-y-2" style={{ minWidth: dayMinWidth(day.courtCount) }}>
        {/* `px-2` mirrors the rows' own `p-2` so the header sits over the same content box the
            columns below are laid out in — without it the two drift apart by the padding. */}
        <div className="flex gap-2 px-2">
          <div className={TIME_GUTTER} />
          {Array.from({ length: day.courtCount }, (_, i) => (
            <div
              key={i}
              className={cn('text-center text-sm font-medium', COURT_WIDTH)}
            >
              Cancha {i + 1}
            </div>
          ))}
        </div>

        {day.rows.map((row) => (
          <DayRow
            key={row.time}
            row={row}
            badgeOf={badgeOf}
            pendingMatchIds={pendingMatchIds}
            editDisabled={editDisabled}
            onEditMatch={onEditMatch}
            placeholderLabels={placeholderLabels}
            verdict={dragging ? dropVerdictFor(dragging, row.cell) : null}
            onShowAvailableTeams={onShowAvailableTeams}
          />
        ))}
      </div>
    </div>
  );
}

interface DayRowProps {
  row: DayRowModel;
  badgeOf: (id: string) => TournamentBadge;
  pendingMatchIds: Set<string>;
  editDisabled: boolean;
  onEditMatch: (match: GridMatch) => void;
  /** matchId -> qualifier labels for its undecided bracket slots. */
  placeholderLabels: PlaceholderLabelMap;
  /**
   * Resolved by the parent, not here. `DayRow` calls `useDroppable`, so it re-renders on every
   * pointer-move tick of a drag; computing the verdict inside would re-run both availability
   * scans per row per tick. `GridDayView` subscribes to no dnd-kit hook, so it only re-renders
   * when the drag starts and ends. Same split the weekly matrix already uses.
   */
  verdict: DropVerdict | null;
  /** Opens the "who can play" list for a slot. */
  onShowAvailableTeams: (slot: GridSlotRef) => void;
}

/**
 * One time slot, and the whole drop target for that time.
 *
 * The droppable id is the same `cellKey(date, time)` the weekly matrix uses, so `handleDragEnd`
 * in `SharedGridView` needs no branch for which view produced the drop. That also means the two
 * views must never be mounted at the same time: duplicate droppable ids silently destroy each
 * other's registration in dnd-kit.
 */
function DayRow({
  row,
  badgeOf,
  pendingMatchIds,
  editDisabled,
  onEditMatch,
  placeholderLabels,
  verdict,
  onShowAvailableTeams,
}: DayRowProps) {
  const { setNodeRef } = useDroppable({ id: row.cell.key });

  return (
    <div
      ref={setNodeRef}
      className={cn(
        'relative flex gap-2 rounded-md border p-2',
        cellStateClasses(row.cell, verdict),
      )}
    >
      <div className={TIME_GUTTER}>
        <div className="flex items-center gap-1.5 text-sm font-medium text-gray-600">
          {row.time}
          <GridAvailableTeamsButton
            date={row.cell.date}
            time={row.time}
            onShowAvailableTeams={onShowAvailableTeams}
            compact
          />
        </div>
        <div
          className={cn(
            'flex items-center gap-1 text-[11px]',
            row.cell.overbooked ? 'text-red-700' : 'text-gray-400',
          )}
        >
          {row.cell.overbooked && <AlertTriangle className="h-3 w-3" />}
          {capacityLabel(row.cell)}
        </div>
      </div>

      {row.courts.map((court, i) => {
        const match = court.match;
        const badge = match ? badgeOf(match.tournamentId) : null;
        return (
          <div
            key={i}
            className={cn(
              'rounded-md',
              COURT_WIDTH,
              court.overflow && 'bg-red-50 ring-1 ring-red-300',
            )}
          >
            {match && badge ? (
              <GridMatchCard
                match={match}
                tournamentName={badge.name}
                colorIndex={badge.colorIndex}
                date={row.cell.date}
                time={row.cell.startTime}
                pending={pendingMatchIds.has(match.matchId)}
                editDisabled={editDisabled}
                placeholders={placeholderLabels.get(match.matchId)}
                onEdit={onEditMatch}
              />
            ) : (
              <div className="h-full min-h-[64px] rounded-md border border-dashed border-gray-200" />
            )}
          </div>
        );
      })}

      {/* Absolutely positioned, and that is the whole point: the row must not change size when a
          drag starts. As a flex sibling this paragraph had nowhere to go — the container is only as
          wide as the courts need, and the courts refuse to shrink past `min-w-[180px]` — so it
          collapsed to its longest word and wrapped into a dozen lines, adding ~200px to every
          warning row. Every row above the dragged card grew, the card moved down with them, and
          dnd-kit's layout-shift compensation (on by default) scrolled the page by exactly that
          delta: press and hold on a phone, and the day shot to the bottom. Out of flow it costs no
          height at all, and it also stops narrowing the courts mid-drag on a wide screen.

          `left-2` and not `inset-x-2`: an absolutely positioned box with only one horizontal offset
          shrinks to fit its text, so the strip covers the card bottoms only as far as it has to. */}
      {verdict?.reason && (
        <p
          className={cn(
            'pointer-events-none absolute bottom-1 left-2 max-w-full truncate rounded bg-white/85 px-1 text-[11px]',
            verdictReasonClass(verdict),
          )}
        >
          {verdict.reason}
        </p>
      )}
    </div>
  );
}
